import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useResponsive } from '@flipster/react/misc/responsive/legacy';

import { marketPageBunja, marketSymbolBunja } from '../../state';
import { Sort } from '../../types';
import { DesktopTable } from './desktop/desktop-table';
import { MobileList } from './mobile/mobile-list';
import { NoFavoriteData } from './no-data/no-favorite-data';
import { marketTableBunja } from './state';

interface Props {
  scrollToList: () => void;
}

/**
 * 테이블 데이터 갱신 기준
 * https://www.figma.com/file/noSJIXIVde1loHn5Z8rMsv/Flipster-App-Sprint-Designs-4Q-%2223?type=design&node-id=32571-18176&mode=dev
 */
export const MarketTable = ({ scrollToList }: Props) => {
  const {
    searchModeAtom,
    discoverCursorAtom,
    setCursorPage: setPage,
    setDiscoverCursorSort,
  } = useBunja(marketPageBunja);
  const { tab: discoverTab } = useAtomValue(discoverCursorAtom);
  const isSearchMode = useAtomValue(searchModeAtom);

  const { isDesktop } = useResponsive();
  const { isLoadingAtom, refetchSymbolsByMarketTab: refetchSymbols } = useBunja(marketSymbolBunja);
  const { paginationAtom, paginatedItemsAtom } = useBunja(marketTableBunja);
  const isLoading = useAtomValue(isLoadingAtom);
  const pagination = useAtomValue(paginationAtom);
  const paginatedItems = useAtomValue(paginatedItemsAtom);

  const onChangePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(window.location.search);
    if (nextPage > 0) nextParams.set('page', String(nextPage));
    else nextParams.delete('page');
    window.history.replaceState(null, '', `?${nextParams}`);
    setPage(nextPage);
    requestAnimationFrame(() => scrollToList());
  };

  const getPageHref = useCallback((page: number) => {
    const nextParams = new URLSearchParams(window.location.search);
    if (page > 0) nextParams.set('page', String(page));
    else nextParams.delete('page');
    return `?${nextParams}`;
  }, []);

  const onChangeSort = (nextSort: Sort) => {
    const nextParams = new URLSearchParams(window.location.search);
    nextParams.delete('page');
    window.history.replaceState(null, '', `?${nextParams}`);
    setDiscoverCursorSort(nextSort);
    refetchSymbols();
    requestAnimationFrame(() => scrollToList());
  };

  if (discoverTab === 'favorites' && (!paginatedItems || paginatedItems.length === 0) && !isSearchMode)
    return <NoFavoriteData />;

  if (isDesktop) {
    return (
      <DesktopTable
        tab={discoverTab}
        items={paginatedItems}
        pagination={pagination}
        setPage={onChangePage}
        getPageHref={getPageHref}
        isLoading={isLoading}
        isSearching={isSearchMode}
        onChangeSort={onChangeSort}
      />
    );
  }

  return (
    <MobileList
      tab={discoverTab}
      items={paginatedItems}
      pagination={pagination}
      setPage={onChangePage}
      getPageHref={getPageHref}
      isLoading={isLoading}
      isSearching={isSearchMode}
      onChangeSort={onChangeSort}
    />
  );
};
